import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { NoContractDraftData } from 'src/generated/services/TFinancialApi';
import { useActiveNoContractDraft } from 'src/use-cases/invoice/useNoContract';
import { checkShouldFetchDraft } from 'src/utils/invoiceMapper';
import { useShipmentSelector } from '../ShipmentSelector';

export interface INoRateCardSelectorProps {
  children: ReactNode;
}

export interface INoRateCardSelectorData {
  isShowModal: boolean;
  isHaveDraft: boolean;
  setupDraft(data: NoContractDraftData): void;
  showNoRateCard(): void;
  closeNoRateCard(): void;
}

export const NoRateCardSelectorContext = createContext<INoRateCardSelectorData>(null);

export const NoRateCardSelector = ({ children }: INoRateCardSelectorProps) => {
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [isHaveDraft, setIsHaveDraft] = useState<boolean>(false);

  // Auto-fetch and setup draft based on current shipment
  const { shipment } = useShipmentSelector();
  const shouldFetchDraft = checkShouldFetchDraft(shipment?.currentDeviationWorkflow);
  const { data: draft } = useActiveNoContractDraft(shouldFetchDraft ? shipment?.shipmentInvoiceId : null);

  // Automatically sync draft state when draft data changes
  useEffect(() => {
    if (draft?.data) {
      setIsHaveDraft(true);
    } else {
      setIsHaveDraft(false);
    }
  }, [draft]);

  const showNoRateCard = useCallback(() => {
    setIsShowModal(true);
  }, []);

  const closeNoRateCard = useCallback(() => {
    setIsShowModal(false);
  }, []);

  // Keep setupDraft for backward compatibility (but it's now redundant)
  const setupDraft = useCallback((data: NoContractDraftData) => {
    if (data) {
      setIsHaveDraft(true);
    } else {
      setIsHaveDraft(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      isShowModal,
      isHaveDraft,
      setupDraft,
      showNoRateCard,
      closeNoRateCard,
    }),
    [isShowModal, isHaveDraft, setupDraft, showNoRateCard, closeNoRateCard]
  );

  return <NoRateCardSelectorContext.Provider value={value}>{children}</NoRateCardSelectorContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useNoRateCardSelector() {
  const context = useContext(NoRateCardSelectorContext);

  if (!context) {
    throw new Error(`No Contract selector compound components cannot be rendered outside the No Contract Selector component`);
  }
  return context;
}
